import {
    Component,
    inject,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
} from '@angular/core';
import {
    AgChartOptions,
    AgBarSeriesLabelOptions,
    AgNodeClickEvent,
} from 'ag-charts-types';
import { AuthService } from '@services/auth/auth.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    ClaimQueryControllerService,
    NeverWorkedClaimResponse,
    NeverWorkedClaimsRequest,
} from '@core/generated/openapi';
import { AgCharts } from 'ag-charts-angular';
import {
    ButtonModule,
    ButtonStyle,
    CardModule,
    DirectivesModule,
    Option,
} from 'rcp-common-lib';
import { isNil } from 'lodash-es';
import { NavigationBehaviorOptions, Router } from '@angular/router';
import { LAST_WORKED_AGE_SEARCH_OPTIONS } from '@core/constants/claim-search.constants';
import { STALLED_CLAIM_FILTER_IDS } from '@core/constants/stalled-claim-filter.constants';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { skip } from 'rxjs';

enum Ranges {
    RANGE_31 = '31-60',
    RANGE_61 = '61-90',
    RANGE_91 = '91-120',
    RANGE_121 = '121+',
}

@UntilDestroy()
@Component({
    selector: 'rcp-oup-never-worked-age-metric',
    imports: [AgCharts, CardModule, DirectivesModule, ButtonModule],
    templateUrl: './never-worked-age-metric.component.html',
    styleUrl: './never-worked-age-metric.component.scss',
})
export class NeverWorkedAgeMetricComponent implements OnInit {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    @Input() claimStalledFilterId: string;

    isLoading: boolean = false;
    chartOptions: AgChartOptions;
    locations: number[] = [];
    fillColors = ['#EEBA05', '#F79703', '#EE413E', '#A92C26'];
    fillColorIndex = 0;

    constructor(
        private authService: AuthService,
        private claimQueryControllerService: ClaimQueryControllerService,
        @Inject(LOCALE_ID) private locale: string,
        private router: Router,
    ) {}

    ngOnInit() {
        this.getSelectedLocations();
        this.onStalledFilterChange();
    }

    getSelectedLocations() {
        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this))
            .subscribe((locations) => {
                if (this.authService.isAgingUploadByLocationOff(locations)) {
                    this.locations = [];
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    !locations.every((val) => val === undefined)
                ) {
                    this.locations = locations;
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    locations.every((val) => val === undefined)
                ) {
                    this.loadData();
                }
            });
    }

    onStalledFilterChange() {
        if (this.claimStalledFilterId) {
            this.claimStalledFilterService
                .onFilterStatusChange(this.claimStalledFilterId)
                .pipe(untilDestroyed(this), skip(1))
                .subscribe({
                    next: () => this.loadData(),
                });
        }
    }

    loadData() {
        this.isLoading = true;
        this.fillColorIndex = 0;
        const request: NeverWorkedClaimsRequest = {
            accountLocationIds: this.locations,
            nonStalledClaimsOnly:
                !this.claimStalledFilterService.getFilterStatusValue(
                    this.claimStalledFilterId,
                ),
        };

        this.claimQueryControllerService
            .getNeverWorkedClaims(request)
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                if (!isNil(data)) {
                    this.createChart(this.setupChartData(data));
                }
                this.isLoading = false;
            });
    }

    setupChartData(dataResponse: NeverWorkedClaimResponse) {
        return [
            {
                count: dataResponse.age31To60ClaimCount,
                total: dataResponse.age31To60TotalAmount,
                range: Ranges.RANGE_31,
            },
            {
                count: dataResponse.age61To90ClaimCount,
                total: dataResponse.age61To90TotalAmount,
                range: Ranges.RANGE_61,
            },
            {
                count: dataResponse.age91To120ClaimCount,
                total: dataResponse.age91To120TotalAmount,
                range: Ranges.RANGE_91,
            },
            {
                count: dataResponse.age120PlusClaimCount,
                total: dataResponse.age120PlusTotalAmount,
                range: Ranges.RANGE_121,
            },
        ];
    }

    getLabelSettings(): AgBarSeriesLabelOptions<any, any> {
        return {
            enabled: true,
            color: '#000',
            placement: 'outside-end',
            padding: 1,
            formatter: (params) => {
                return params.datum?.count.toLocaleString('en-US');
            },
            fontSize: 12,
        };
    }

    formatCurrency(value: number, useCompact = true) {
        const currencyFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            notation: useCompact ? 'compact' : 'standard',
        });

        return currencyFormatter.format(value);
    }

    createChart(data: any) {
        this.chartOptions = {
            axes: [
                {
                    type: 'category',
                    position: 'bottom',
                    keys: ['range'],
                },
                {
                    type: 'number',
                    position: 'left',
                    label: {
                        formatter: ({ value }) => {
                            return this.formatCurrency(value);
                        },
                    },
                    interval: {
                        minSpacing: 20,
                        maxSpacing: 25,
                    },
                },
            ],
            data: data,
            series: [
                {
                    type: 'bar',
                    xKey: 'range',
                    yKey: 'total',
                    yName: 'Total Claims',
                    itemStyler: (params) => {
                        const index = this.chartOptions.data.findIndex(
                            (item) => item === params.datum,
                        );
                        params.fill =
                            this.fillColors[index % this.fillColors.length];
                        return params;
                    },
                    label: this.getLabelSettings(),
                    tooltip: {
                        renderer: ({ datum, xKey, yKey }) => {
                            return {
                                title: `${datum['count'].toLocaleString('en-US')} Claims - ${this.formatCurrency(datum[yKey], false)}`,
                                heading: `${datum['range']} Days`,
                                data: [],
                            };
                        },
                    },
                    listeners: {
                        nodeClick: (params) => this.handleClick(params),
                    },
                },
            ],
        };
    }

    handleClick(params: AgNodeClickEvent<'nodeClick', any>) {
        const range = params.datum?.range;
        let ageRangeOption: Option;

        switch (range) {
            case Ranges.RANGE_31:
                ageRangeOption = LAST_WORKED_AGE_SEARCH_OPTIONS[1];
                break;
            case Ranges.RANGE_61:
                ageRangeOption = LAST_WORKED_AGE_SEARCH_OPTIONS[2];
                break;
            case Ranges.RANGE_91:
                ageRangeOption = LAST_WORKED_AGE_SEARCH_OPTIONS[3];
                break;
            case Ranges.RANGE_121:
                ageRangeOption = LAST_WORKED_AGE_SEARCH_OPTIONS[4];
                break;
            default:
                break;
        }

        this.setByLastWorkedStalledFilter();

        this.router.navigateByUrl('/aging/claims/search/lastWorked/0', <
            NavigationBehaviorOptions
        >{
            state: {
                claimAgeValueFromRoute: ageRangeOption,
            },
        });
    }

    viewDetailsAll() {
        this.setByLastWorkedStalledFilter();
        this.router.navigateByUrl('/aging/claims/search/lastWorked/0', <
            NavigationBehaviorOptions
        >{
            state: {
                claimAgeValueFromRoute: LAST_WORKED_AGE_SEARCH_OPTIONS[0],
            },
        });
    }

    setByLastWorkedStalledFilter() {
        this.claimStalledFilterService.updateFilterStatus(
            STALLED_CLAIM_FILTER_IDS.BY_LAST_WORKED,
            this.claimStalledFilterService.getFilterStatusValue(
                this.claimStalledFilterId,
            ),
        );
    }

    protected readonly ButtonStyle = ButtonStyle;
}
